import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';

import { meterReadingsImportApi } from '../../api/meterReadingsImport';
import { downloadBlobAsFile } from '../../utils/download';
import ConfirmDialog from '../ConfirmModal';
import { $canEditComplex } from '../../store/auth';
import { showSoftSuccess } from '../../utils/softSuccess';

export default function MeterReadingsImportDialog({ open, onClose, onImported, buildingId }) {
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('history');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [rollingBackId, setRollingBackId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBatch, setConfirmBatch] = useState(null);
  const navigate = useNavigate();
  const canRollback = useUnit($canEditComplex);

  const resetState = () => {
    setFile(null);
    setImportMode('history');
    setError('');
    setResult(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose?.();
  };

  const canUpload = !!file && !loading;
  const isXmlFile = !!file && String(file.name).toLowerCase().endsWith('.xml');

  const errorsPreview = useMemo(() => {
    if (!result?.errors?.length) return [];
    return result.errors.slice(0, 10);
  }, [result]);

  const handleDownloadTemplate = async () => {
    try {
      setError('');
      const blob = await meterReadingsImportApi.downloadTemplate();
      downloadBlobAsFile(blob, 'meter_readings_template.xlsx');
    } catch (err) {
      console.error('template download failed', err);
      setError('Не удалось скачать шаблон. Попробуйте позже.');
    }
  };

  const handleUpload = async () => {
    if (!file || loading) return;
    try {
      setLoading(true);
      setError('');
      const res = await meterReadingsImportApi.uploadFile(file, {
        import_mode: importMode,
        building_id: buildingId ? String(buildingId) : undefined,
      });
      setResult(res);
      onImported?.(res);
      showSoftSuccess(res?.errors?.length ? 'Импорт с ошибками' : 'Импорт готов');
      await loadBatches();
    } catch (err) {
      console.error('import failed', err);
      setError('Не удалось загрузить файл. Проверьте формат и попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const data = await meterReadingsImportApi.listBatches({ limit: 10, offset: 0 });
      setBatches(data?.items || []);
    } catch (err) {
      console.error('load batches failed', err);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadBatches();
    }
  }, [open]);

  const requestRollback = (batch) => {
    if (!canRollback) return;
    setConfirmBatch(batch);
    setConfirmOpen(true);
  };

  const handleRollback = async () => {
    if (!confirmBatch?.id || rollingBackId) return;
    try {
      setRollingBackId(confirmBatch.id);
      await meterReadingsImportApi.rollbackBatch(confirmBatch.id);
      await loadBatches();
      onImported?.();
    } catch (err) {
      console.error('rollback failed', err);
      setError('Не удалось откатить импорт. Попробуйте позже.');
    } finally {
      setRollingBackId(null);
      setConfirmOpen(false);
      setConfirmBatch(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Импорт показаний</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            CSV/XLSX: <strong>serial_number</strong> или <strong>dev_eui</strong>, <strong>value</strong>,{' '}
            <strong>reading_date</strong>. XML: выгрузка в формате <strong>ISTA</strong>.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Сценарий импорта
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={importMode}
              onChange={(_, value) => {
                if (!value) return;
                setImportMode(value);
                setResult(null);
                setError('');
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                '& .MuiToggleButton-root': {
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  borderRadius: '14px !important',
                  border: '1px solid rgba(15, 23, 42, 0.14)',
                  px: 1.5,
                  py: 1.25,
                },
              }}
            >
              <ToggleButton value="current">
                <Stack spacing={0.35}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Текущие показания
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Для XML будет загружено только актуальное показание каждого прибора.
                  </Typography>
                </Stack>
              </ToggleButton>
              <ToggleButton value="history">
                <Stack spacing={0.35}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Показания с историей
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Для XML будут загружены текущие и архивные значения по датам без дублей.
                  </Typography>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={handleDownloadTemplate}
              disabled={loading}
            >
              Скачать шаблон
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              component="label"
              disabled={loading}
            >
              Выбрать файл
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xml,.zip"
                onChange={(e) => {
                  const next = e.target.files?.[0] || null;
                  setFile(next);
                  setResult(null);
                  setError('');
                }}
              />
            </Button>
          </Stack>

          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {file ? `Файл выбран: ${file.name}` : 'Файл не выбран'}
            </Typography>
            {isXmlFile && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<DataObjectRoundedIcon sx={{ fontSize: 14 }} />}
                  label="XML"
                />
                <Chip size="small" color="info" variant="outlined" label="ISTA" />
              </Stack>
            )}
          </Box>

          {result && (
            <Box>
              <Alert severity={result.errors?.length ? 'warning' : 'success'}>
                Строк {result.total_rows}, ок {result.imported_rows}
                {result.errors?.length ? `, ошибок ${result.errors.length}` : ''}
              </Alert>

              {!!result.warnings?.length && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  <Stack spacing={0.5}>
                    {result.warnings.map((warning) => (
                      <Typography key={warning} variant="body2">
                        {warning}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              {errorsPreview.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Ошибки (первые {errorsPreview.length}):
                  </Typography>
                  <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
                    {errorsPreview.map((item, idx) => (
                      <React.Fragment key={`${item.row_index}-${idx}`}>
                        <ListItem>
                          <ListItemText
                            primary={`Строка ${item.row_index}: ${item.message}`}
                            secondary={
                              item.serial_number || item.dev_eui
                                ? `ID: ${item.serial_number || item.dev_eui}`
                                : null
                            }
                          />
                        </ListItem>
                        {idx < errorsPreview.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                  {result.errors.length > errorsPreview.length && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      и ещё {result.errors.length - errorsPreview.length} ошибок…
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Divider />

          <Stack direction="row" alignItems="center" spacing={1}>
            <HistoryRoundedIcon fontSize="small" />
            <Typography variant="subtitle2">История импортов</Typography>
            {loadingBatches && <CircularProgress size={16} />}
            <Button
              size="small"
              startIcon={<OpenInNewRoundedIcon fontSize="small" />}
              onClick={() => navigate('/meter-readings/imports')}
              sx={{ marginLeft: 'auto' }}
            >
              Открыть
            </Button>
          </Stack>

          {batches.length === 0 && !loadingBatches && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              История пока пуста.
            </Typography>
          )}

          {batches.length > 0 && (
            <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
              {batches.map((batch, idx) => {
                const rolledBack = !!batch.rolled_back_at;
                const rowText = [
                  batch.file_name || 'Импорт',
                  `строк: ${batch.total_rows ?? '—'}`,
                  `успешно: ${batch.imported_rows ?? '—'}`,
                ].join(' · ');
                const author = batch.created_by_email || batch.created_by_user_id;
                const rolledBy = batch.rolled_back_by_email || batch.rolled_back_by_user_id;
                return (
                  <React.Fragment key={batch.id}>
                    <ListItem
                      secondaryAction={
                        rolledBack ? (
                          <Chip
                            size="small"
                            label={rolledBy ? `Откат: ${rolledBy}` : 'Откат выполнен'}
                          />
                        ) : (
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => requestRollback(batch)}
                            disabled={rollingBackId === batch.id || !canRollback}
                            title="Откатить импорт"
                            sx={{ visibility: canRollback ? 'visible' : 'hidden' }}
                          >
                            {rollingBackId === batch.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <UndoRoundedIcon fontSize="small" />
                            )}
                          </IconButton>
                        )
                      }
                    >
                      <ListItemText
                        primary={rowText}
                        secondary={`Дата: ${formatDate(batch.created_at)}${author ? ` · Автор: ${author}` : ''}`}
                      />
                    </ListItem>
                    {idx < batches.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Закрыть
        </Button>
        <Button variant="contained" onClick={handleUpload} disabled={!canUpload}>
          Загрузить
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmOpen}
        title="Откатить импорт?"
        description={`Откатить импорт ${confirmBatch?.file_name ? `«${confirmBatch.file_name}»` : ''}? Все показания из этой загрузки будут удалены.`}
        confirmText="Откатить"
        confirmColor="error"
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmBatch(null);
        }}
        onConfirm={handleRollback}
      />
    </Dialog>
  );
}
